import React, { useState } from 'react';
import { copyToClipboard } from '@/shared/utils/exportUtils';

export default function AdminCDNSection({ appId, showToast }) {
  const [copiedItem, setCopiedItem] = useState(null);

  const cdnBaseUrl = `https://zapt-cdn.com/${appId}`;
  const exampleUrl = `${cdnBaseUrl}/example-image.png`;

  const items = [
    {
      label: 'CDN Base URL',
      value: cdnBaseUrl,
      description: 'Base URL for accessing assets in this app\'s CDN folder'
    },
    {
      label: 'Example Asset URL',
      value: exampleUrl,
      description: 'Example of how to access a file (replace with your actual filename)'
    }
  ];

  const handleCopy = async (value, label) => {
    const result = await copyToClipboard(value);
    if (result.success) {
      setCopiedItem(label);
      if (showToast) {
        showToast(`Copied: ${value}`, 'success', 2000);
      }
      setTimeout(() => setCopiedItem(null), 2000);
    } else {
      if (showToast) {
        showToast('Failed to copy URL', 'error');
      }
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span>📦</span>
          <span>CDN Assets</span>
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Access static assets for this app via the CDN
        </p>
      </div>

      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-900">
          <span className="font-semibold">Note:</span> Assets are stored in the 'zapt-user-app-assets' bucket on Backblaze, with Cloudflare providing CDN functionality. Each app has its own folder in the bucket. Upload files to your app's folder to make them accessible via these URLs.{' '}
          <a
            href="https://tree-ams5-0001.secure.backblaze.com/b2_browse_files2.htm?bucketId=b178edbf7c67d125948b0d1e"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-blue-700 hover:text-blue-900 underline"
          >
            Browse Backblaze CDN bucket →
          </a>
        </p>
      </div>

      <div className="space-y-3">
        {items.map(({ label, value, description }) => (
          <div
            key={label}
            className="flex items-start justify-between gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors group"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 mb-1">{label}</p>
              <p className="text-xs text-gray-500 mb-2">{description}</p>
              <code className="text-sm text-gray-800 font-mono bg-white px-2 py-1 rounded border border-gray-300 block break-all">
                {value}
              </code>
            </div>
            <button
              onClick={() => handleCopy(value, label)}
              className="ml-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2 flex-shrink-0"
            >
              {copiedItem === label ? (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-xs text-gray-600">
          <span className="font-semibold">Usage:</span> Reference assets in your app using the format <code className="bg-white px-1 py-0.5 rounded border border-gray-300 text-gray-800">https://zapt-cdn.com/{appId}/[filename]</code>. Make sure files are uploaded to your app's folder in the Backblaze bucket.
        </p>
      </div>
    </div>
  );
}
